import { useAuth, useRequest } from '@polvo-labs/react-auth'
import { format } from 'date-fns'
import React, { useState } from 'react'
import { toast } from 'react-toastify'
import routes from '../../../config/routes'
import ProposalForm from '../../../forms/Proposal'
import StatusMessageForm from '../../../forms/Realty/StatusMessage'
import Realty, { STATUS, Status, STATUS_COLORS } from '../../../models/Realty'
import DisplayView from '../../../screens/Admin/DisplayView'
import { currencyFormat } from '../../../utils/form-validations'
import isStaff from '../../../utils/is-staff'
import Button from '../../Button'
import DescriptionList from '../../DescriptionList'
import Icon from '../../Icon'
import Loader from '../../Loader'
import Modal from '../../Modal'
import StatusFlow from '../../StatusFlow'
import Text from '../../Text'
import RealtyProposals from '../Proposal'
import { Container, StatusButtons } from './styles'

interface Props {
  realty: Realty
  onUpdated?: () => void
  adminMode?: boolean
}

const RealtyDetails: React.FC<Props> = ({ realty, onUpdated, adminMode }) => {
  const { user } = useAuth()
  const [loader, setLoader] = useState(false)
  const [open, setOpen] = useState(false)
  const [afterMessage, setAfterMessage] = useState(() => { })
  const [message, setMessage] = useState<string>('')
  const { request } = useRequest()

  const addressText = realty.address ? `${realty.address.street}, ${realty.address.number} - ${realty.address.complement} - ${realty.address.neighborhood} - ${realty.address.city} - ${realty.address.state}`.split('null, ').join('').split(' null -').join('') : '-'
  const addressParam = realty.address ? `${realty.address.street}, ${realty.address.number} - ${realty.address.complement} - ${realty.address.neighborhood} - ${realty.address.city} - ${realty.address.state}, ${realty.address.zip_code}, Brasil`.split('null, ').join('').split(' null -').join('') : '-'

  const googleLink = () => {
    if (addressParam !== '-')
      return (<a
        target="_blank"
        rel="noopener noreferrer"
        href={`https://www.google.com/maps/?q=${addressParam}`}
      > {addressText}</a>)
    else
      return addressText
  }

  const changeStatus = (status: string, realty: Realty, onUpdated: (() => void) | undefined, message = '') => {
    setLoader(true)
    request({
      method: 'PUT',
      url: `${process.env.REACT_APP_API_URL}/realties/status/${status}`,
      data: {
        ...realty,
        realty_id: realty.id,
        status: status,
        message: message,
      },
    }).then(() => {
      if (onUpdated) {
        onUpdated()
      }
      toast.success(`${STATUS[status as Status]} com sucesso`)
    }).catch(() => {
      toast.error('Não foi possível salvar')
    }).finally(() => {
      setLoader(false)
    })
  }


  return (
    <Container>
      {!adminMode && (
        <Text
          tag="h2"
          color="darkBlue"
        >
          Imóvel #{realty.id.toString().padStart(8, '0')}
        </Text>
      )}

      {adminMode ? (
        <div
          style={{ marginTop: 20 }}
        >
          <DisplayView
            items={[
              [
                {
                  label: 'ID',
                  value: realty.id,
                  size: 30,
                },
                {
                  label: 'Informação Fiscal',
                  value: realty.fiscal_information || '-',
                  size: 35,
                },
                {
                  label: 'Matrícula',
                  value: realty.registration_number || '-',
                  size: 35,
                },
              ],
              [
                {
                  label: 'Endereço',
                  value: googleLink(),
                  size: 100,
                },
              ],
              [
                {
                  label: 'Tipo de imóvel',
                  value: `${realty.type} ${realty.custom_type ? `- ${realty.custom_type}` : ''}`,
                  size: 25,
                },
                {
                  label: 'Deve IPTU desde',
                  value: realty.owes_since || '-',
                  size: 25,
                },
                {
                  label: 'Status',
                  value: STATUS[realty.status] || '?',
                  size: 50,
                  color: STATUS_COLORS[realty.status] as string,
                },
              ],
              [
                {
                  label: 'Data de cadastro',
                  value: format(new Date(realty.created_at), 'dd/MM/yyyy HH:mm'),
                  size: 50,
                },
                {
                  label: 'Valor da proposta',
                  value: realty.proposal_amount ? `R$ ${currencyFormat(realty.proposal_amount)}` : '-',
                  size: 50,
                },
              ],
              ...[(['Lote', 'Área', 'Chácara', 'Outros'].includes(realty.type) ? [
                {
                  label: 'Números',
                  value: realty.numbers || '-',
                  size: 33.33,
                },
                {
                  label: 'Quantidade',
                  value: realty.amout || '-',
                  size: 33.33,
                },
                {
                  label: 'Quadras',
                  value: realty.blocks || '-',
                  size: 33.33,
                },
              ] : [])],
              [
                {
                  label: 'Usuário',
                  value: realty.user?.name || '-',
                  size: 50,
                  url: routes.admin.user.show.pathFor(realty.user?.id),
                },
                {
                  label: 'E-mail do usuário',
                  value: realty.user?.email || '-',
                  size: 50,
                },
              ],
            ]}
          />
          <StatusFlow
            history={realty.statusHistory}
          />
          <RealtyProposals
            realty={realty}
          />

          {isStaff(user) && realty.status === 'analysing' && (
            <div>
              <Text
                tag="h2"
                color="darkGray"
                style={{
                  marginTop: 20,
                }}
              >
                Enviar proposta
              </Text>
              <ProposalForm
                initialValues={{
                  value: realty.proposal_amount || 0,
                }}
                realty={realty}
                onUpdated={onUpdated ? onUpdated : () => null}
              />
            </div>
          )}
        </div>
      ) : (
        <DescriptionList
          big
          items={[{
            term: 'Situação',
            description: STATUS[realty.status],
            color: STATUS_COLORS[realty.status] as any,
          }, {
            term: 'Tipo do imóvel',
            description: `${realty.type} ${realty.custom_type ? `- ${realty.custom_type}` : ''}`,
          }, {
            term: 'Data de cadastro',
            description: format(new Date(realty.created_at), 'dd/MM/yyyy HH:mm'),
          }, {
            term: 'Matrícula',
            description: realty.registration_number || '-',
          }, {
            term: 'Informação Fiscal',
            description: realty.fiscal_information || '-',
          },
          ...(['Lote', 'Área', 'Chácara', 'Outros'].includes(realty.type) ? [{
            term: 'Números',
            description: realty.numbers || '-',
          }, {
            term: 'Quantidade',
            description: realty.amout || '-',
          }, {
            term: 'Quadras',
            description: realty.blocks || '-',
          }] : []), {
            term: 'Deve IPTU desde',
            description: realty.owes_since || '-',
          }, {
            term: 'Endereço',
            description: realty.address ? `${realty.address.street}, ${realty.address.number} - ${realty.address.complement} - ${realty.address.neighborhood} - ${realty.address.city} - ${realty.address.state}` : '-',
          }]}
        />
      )}

      {realty.uploads && !!realty.uploads.length && (
        <div>
          <Text
            tag="h2"
            color="darkGray"
            style={{
              marginTop: 20,
            }}
          >
            Arquivos compartilhados
          </Text>
          {realty.uploads.map(up => (
            <Button
              key={up.id}
              before={(
                <Icon
                  name="attachment-blue"
                />
              )}
              label={up.name}
              backgroundColor="lightBluishGray"
              fontColor="darkBlue"
              alignText="flex-start"
              block
              onClick={() => window.open(up.url, '_blank')}
            />
          ))}
        </div>
      )}

      {isStaff(user) && !!realty.possibleStatus?.length && (
        <div
          style={{ marginTop: 30 }}
        >
          <Text
            tag="h2"
            color="darkGray"
            style={{
              marginTop: 20,
            }}
          >
            Alterar status para:
          </Text>

          <StatusButtons>
            {Object
              .keys(STATUS)
              .filter(s => s !== 'proposal' && realty.possibleStatus?.includes(s as Status))
              .map((key: string) => (
                <Button
                  key={key}
                  label={STATUS[key as Status]}
                  fontColor="white"
                  backgroundColor={STATUS_COLORS[key as Status] as any}
                  onClick={() => {
                    if (key === 'pending') {
                      setOpen(true)
                    } else {
                      changeStatus(key, realty, onUpdated)
                    }
                  }}
                />
              ))}
          </StatusButtons>

          {loader && (
            <Loader
              full
            />
          )}
        </div>
      )}
      <Modal
        title="Escreva sua mensagem"
        open={open}
        onToggle={open => {
          setOpen(open)
        }}
      >
        <Container>
          <StatusMessageForm
            onSubmit={(data) => {
              changeStatus('pending', realty, onUpdated, data.message)
            }}
          >
          </StatusMessageForm>
        </Container>
      </Modal >
    </Container >
  )
}

export default RealtyDetails
